import { useCallback, useContext, useMemo } from 'react';
import { gate } from 'hub-http/gates';
import { Flex } from 'foundations-components/components/Flex';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { useIsUngated } from 'ai-components-ui-library/hooks/useIsUngated';
import { View } from 'foundations-components/components/View';
import BreezeTile from '../internal/breeze/BreezeTile';
import { useAbly } from '../../../hooks/useAbly';
import { useGetCopilotPreview } from '../../../hooks/useGetCopilotPreview';
import { AblyContextProvider } from '../../../ably/context/AblyContextProvider';
import { CopilotPreviewDataSection } from './CopilotPreviewDataSection';
import withErrorBoundary from '../../error/withErrorBoundary';
import CopilotPreviewHeader from '../internal/CopilotPreviewHeader';
import SummaryTimestamp from '../internal/SummaryTimestamp';
import BreezeRefreshButton from '../internal/breeze/BreezeRefreshButton';
import { SUCCEEDED } from '../../../types/requestStatus';
import { CopilotPreviewActions } from './CopilotPreviewActions';
import { PreviewsEmptyStateWrapper } from './PreviewsEmptyStateWrapper';
import { getCopyButtonText } from '../internal/utils/CopyButton';
import { CopilotPreviewContext } from '../context/CopilotPreviewContextProvider';
import { usePreviewsFeedback } from './hooks/usePreviewsFeedback';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopilotPreviewAutoGeneratedV2 = props => {
  const {
    hasAIBadge,
    aiTracker,
    copilotAction,
    previewKey,
    previewSource,
    previewTitle,
    showCloseButton,
    summaryType,
    showAskAQuestionButton,
    onCloseButtonClick,
    numHoursTilSummaryStale,
    onChatWithCopilot,
    onPositiveFeedback,
    onNegativeFeedback,
    onRefresh,
    onQuickActionClick,
    usageTrackerAppLocationKey,
    overrideDisabled,
    onDontShowAgain
  } = props;
  const isUngatedForSimplifiedPreviews = useIsUngated(gate('CopilotPreviewsV2:Simplified'));

  // this is added to prevent OpenAI api calls every time the page is loaded
  const numHoursTilSummaryStaleAdjusted = numHoursTilSummaryStale < 1 ? 1 : numHoursTilSummaryStale;
  const {
    onPositiveFeedback: onPositiveFeedbackSimplified,
    onNegativeFeedback: onNegativeFeedbackSimplified
  } = usePreviewsFeedback(previewKey, previewSource);
  const {
    previewResult,
    requestStatus,
    refresh,
    error,
    isHipaaPortal
  } = useGetCopilotPreview(previewKey, previewSource, copilotAction, numHoursTilSummaryStaleAdjusted, summaryType, isUngatedForSimplifiedPreviews);
  const {
    previewData,
    resetPreviewData
  } = useContext(CopilotPreviewContext);
  const {
    isStreamingComplete,
    previewMessages,
    errorCode
  } = previewData;
  const {
    ablyChannelId,
    tokenDetails,
    messages
  } = useMemo(() => {
    let channelId = null;
    let authToken;
    let msgs = [];

    // To differentiate between the two types of preview results, we need to check if the previewResult has a channel property
    if (previewResult && 'channel' in previewResult) {
      const {
        channel,
        authToken: token
      } = previewResult;
      channelId = channel;
      authToken = token;
    } else if (!isUngatedForSimplifiedPreviews && previewResult && 'messages' in previewResult) {
      msgs = previewResult.messages;
    } else if (isUngatedForSimplifiedPreviews && previewResult && 'llmResponse' in previewResult && 'createdAt' in previewResult) {
      msgs = [Object.assign({}, previewResult, {
        createdAt: previewResult.createdAt,
        llmResponse: previewResult.llmResponse
      })];
    }
    return {
      ablyChannelId: channelId,
      tokenDetails: authToken,
      messages: msgs
    };
  }, [isUngatedForSimplifiedPreviews, previewResult]);
  const {
    clientOptions,
    subscriptionHandler
  } = useAbly(ablyChannelId);
  const messagesToRender = useMemo(() => {
    return messages.length > 0 ? messages : previewMessages || [];
  }, [messages, previewMessages]);
  const handleRefresh = useCallback(() => {
    // First reset the streaming message
    resetPreviewData();

    // Then trigger the callback and refresh after reset is complete
    onRefresh === null || onRefresh === void 0 || onRefresh();
    refresh();
    aiTracker.track('Ai Addon Copilot Preview', 'Copilot Preview Interaction', {
      action: 'refresh_preview',
      isCopilotV2Enabled: true
    });
  }, [onRefresh, refresh, resetPreviewData, aiTracker]);
  const copyMessage = getCopyButtonText(messagesToRender[0]);
  const timestamp = messagesToRender.length > 0 ? 'timestamp' in messagesToRender[0] ? messagesToRender[0].timestamp : messagesToRender[0].createdAt : 0;
  const messageId = messagesToRender.length > 0 ? 'id' in messagesToRender[0] ? messagesToRender[0].id : messagesToRender[0].llmResponse[0].id : '';
  const threadId = messagesToRender.length > 0 ? 'threadId' in messagesToRender[0] ? messagesToRender[0].threadId : undefined : undefined;
  return /*#__PURE__*/_jsx(BreezeTile, {
    children: /*#__PURE__*/_jsxs(AblyContextProvider, {
      ablyChannelId: ablyChannelId,
      tokenDetails: tokenDetails,
      clientOptions: clientOptions,
      subscriptionHandler: subscriptionHandler,
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        justifyContent: "space-between",
        marginBlockEnd: "100",
        "data-test-id": "copilot-preview-v2-header",
        children: [/*#__PURE__*/_jsx(CopilotPreviewHeader, {
          previewTitle: previewTitle
        }), hasAIBadge && /*#__PURE__*/_jsx(AIBadge, {})]
      }), /*#__PURE__*/_jsxs(PreviewsEmptyStateWrapper, {
        usageTracker: aiTracker,
        onDontShowAgain: onDontShowAgain,
        overrideDisabled: overrideDisabled,
        previousGeneratedPreview: messages,
        error: error,
        isHipaaPortal: isHipaaPortal,
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          marginBlockEnd: "300",
          children: [(isStreamingComplete || requestStatus === SUCCEEDED) && messagesToRender.length > 0 && /*#__PURE__*/_jsx(SummaryTimestamp, {
            timestamp: timestamp
          }), (isStreamingComplete || requestStatus === SUCCEEDED) && messagesToRender.length > 0 && /*#__PURE__*/_jsx(BreezeRefreshButton, {
            onRefresh: handleRefresh
          })]
        }), /*#__PURE__*/_jsx(CopilotPreviewDataSection, {
          usageTracker: aiTracker,
          messages: messagesToRender.length > 0 && requestStatus === SUCCEEDED ? messagesToRender : [],
          onQuickActionClick: onQuickActionClick
        }), /*#__PURE__*/_jsx(View, {
          paddingBlockStart: "200",
          children: messagesToRender.length > 0 && requestStatus === SUCCEEDED && /*#__PURE__*/_jsx(InlineFeedback, {
            copyMessage: copyMessage,
            messageId: messageId.toString(),
            onPositiveFeedback: isUngatedForSimplifiedPreviews ? onPositiveFeedbackSimplified : onPositiveFeedback,
            onNegativeFeedback: isUngatedForSimplifiedPreviews ? onNegativeFeedbackSimplified : onNegativeFeedback,
            threadId: threadId,
            usageTracker: aiTracker,
            usageTrackerAppLocationKey: usageTrackerAppLocationKey
          })
        }), /*#__PURE__*/_jsx(CopilotPreviewActions, {
          messages: messagesToRender,
          aiTracker: aiTracker,
          showCloseButton: showCloseButton,
          showAskAQuestionButton: showAskAQuestionButton,
          disabled: requestStatus !== SUCCEEDED || !isStreamingComplete && messagesToRender.length === 0 || !!errorCode,
          onChatWithCopilot: onChatWithCopilot,
          onCloseButtonClick: onCloseButtonClick
        })]
      })]
    })
  });
};
export default withErrorBoundary(CopilotPreviewAutoGeneratedV2);